// front/src/app/features/tickets/ticketSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  create_ticket,
  get_ticket,
  get_tickets_list,
  reply_ticket,
  close_ticket,
  claim_ticket,
  assign_ticket,
  // ✅ Phase 13
  get_ticket_assignees,
  // ✅ Phase 15
  get_ticket_history,
} from "../../../services/TicketServices/TicketServices";
import { showToast, showApiErrorToast } from "../../../utils";

export const initialState = {
  loading: false,
  tickets: [],
  ticket: null,
  error: null,

  // ✅ Phase 13
  assignees: [],
  assigneesLoading: false,

  // ✅ Phase 15
  history: [],
  historyLoading: false,
};

// ✅ helper: normalize list payload (array vs paginated object)
const normalizeTicketsList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

// ✅ helper: normalize ticket details payload
const normalizeTicket = (payload) => {
  if (!payload) return null;
  if (payload?.data && typeof payload.data === "object") return payload.data;
  return payload;
};

// ✅ Phase 7: idempotency helper
const genClientRequestId = () => {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch (_) {}
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const ensureClientRequestId = (data) => {
  if (!data) return data;

  // FormData
  if (typeof FormData !== "undefined" && data instanceof FormData) {
    const existing = data.get("client_request_id");
    if (!existing) data.append("client_request_id", genClientRequestId());
    return data;
  }

  // plain object
  if (typeof data === "object") {
    if (!data.client_request_id) data.client_request_id = genClientRequestId();
    return data;
  }

  return data;
};

const getTicketListPathForCurrentPanel = () => {
  try {
    const path = window.location?.pathname || "";
    return path.startsWith("/support/") ? "/support/tickets" : "/ticket";
  } catch (_) {
    return "/ticket";
  }
};

export const getTicketsList = createAsyncThunk(
  "tickets/getTicketsList",
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await get_tickets_list(params);
      return data;
    } catch (ex) {
      console.log(ex);
      return rejectWithValue(ex?.response?.data ?? ex?.message ?? ex);
    }
  }
);

export const addNewTicket = createAsyncThunk(
  "tickets/addNewTicket",
  async (initialData, { rejectWithValue }) => {
    try {
      const payload = ensureClientRequestId(initialData);
      const { status } = await create_ticket(payload);

      if (status >= 200 && status < 300) {
        showToast("success", "تیکت با موفقیت ارسال شد");
        window.location.replace(getTicketListPathForCurrentPanel());
      }

      return status;
    } catch (ex) {
      console.log(ex);
      showApiErrorToast(ex);
      return rejectWithValue(ex?.response?.data ?? ex?.message ?? ex);
    }
  }
);

// ===== legacy reply (kept) =====
export const replyTicket = createAsyncThunk(
  "tickets/replyTicket",
  async ({ ticket_id, initialData }, { rejectWithValue }) => {
    try {
      const payload = ensureClientRequestId(initialData);
      const { status } = await reply_ticket(ticket_id, payload);

      if (status >= 200 && status < 300) {
        showToast("success", "تیکت با موفقیت ارسال شد");
        window.location.replace(getTicketListPathForCurrentPanel());
      }

      return status;
    } catch (ex) {
      console.log(ex);
      showApiErrorToast(ex);
      return rejectWithValue(ex?.response?.data ?? ex?.message ?? ex);
    }
  }
);

// ✅ inline reply (NO redirect) — fixes "replyTicketInline is not defined"
export const replyTicketInline = createAsyncThunk(
  "tickets/replyTicketInline",
  async ({ ticket_id, initialData }, { rejectWithValue }) => {
    try {
      const payload = ensureClientRequestId(initialData);
      const { data, status } = await reply_ticket(ticket_id, payload);

      if (status >= 200 && status < 300) {
        showToast("success", "تیکت با موفقیت ارسال شد");
      }

      return { ticket_id, data, status };
    } catch (ex) {
      console.log(ex);
      showApiErrorToast(ex);
      return rejectWithValue(ex?.response?.data ?? ex?.message ?? ex);
    }
  }
);

export const getTicketById = createAsyncThunk(
  "tickets/getTicketById",
  async (ticket_id, { rejectWithValue }) => {
    try {
      const { data } = await get_ticket(ticket_id);
      return data;
    } catch (ex) {
      console.log(ex);
      return rejectWithValue(ex?.response?.data ?? ex?.message ?? ex);
    }
  }
);

// ===== legacy close (kept) =====
export const closeTicket = createAsyncThunk(
  "tickets/closeTicket",
  async (ticket_id, { rejectWithValue }) => {
    try {
      const { status } = await close_ticket(ticket_id);

      if (status >= 200 && status < 300) {
        showToast("success", "تیکت با موفقیت بسته شد");
        window.location.replace(getTicketListPathForCurrentPanel());
      }

      return { ticket_id, status };
    } catch (ex) {
      console.log(ex);
      showApiErrorToast(ex);
      return rejectWithValue(ex?.response?.data ?? ex?.message ?? ex);
    }
  }
);

// ✅ inline close (NO redirect) — fixes "closeTicketInline is not defined"
export const closeTicketInline = createAsyncThunk(
  "tickets/closeTicketInline",
  async (ticket_id, { rejectWithValue }) => {
    try {
      const { data, status } = await close_ticket(ticket_id);

      if (status >= 200 && status < 300) {
        showToast("success", "تیکت با موفقیت بسته شد");
      }

      return { ticket_id, data, status };
    } catch (ex) {
      console.log(ex);
      showApiErrorToast(ex);
      return rejectWithValue(ex?.response?.data ?? ex?.message ?? ex);
    }
  }
);

// ===== claim ticket =====
export const claimTicket = createAsyncThunk(
  "tickets/claimTicket",
  async (ticket_id, { rejectWithValue }) => {
    try {
      const { data, status } = await claim_ticket(ticket_id);

      if (status >= 200 && status < 300) {
        showToast("success", "تیکت به شما تخصیص داده شد");
      }

      return { ticket_id, data, status };
    } catch (ex) {
      console.log(ex);
      showApiErrorToast(ex);
      return rejectWithValue(ex?.response?.data ?? ex?.message ?? ex);
    }
  }
);

// ===== assign ticket =====
export const assignTicket = createAsyncThunk(
  "tickets/assignTicket",
  async ({ ticket_id, assigned_to_id }, { rejectWithValue }) => {
    try {
      const { data, status } = await assign_ticket(ticket_id, assigned_to_id);

      if (status >= 200 && status < 300) {
        showToast("success", "تخصیص تیکت با موفقیت انجام شد");
      }

      return { ticket_id, data, status, assigned_to_id };
    } catch (ex) {
      console.log(ex);
      showApiErrorToast(ex);
      return rejectWithValue(ex?.response?.data ?? ex?.message ?? ex);
    }
  }
);

// ===== Phase 13: fetch assignees for unitary =====
export const getTicketAssignees = createAsyncThunk(
  "tickets/getTicketAssignees",
  async (unitary, { rejectWithValue }) => {
    try {
      const u = String(unitary || "").trim();
      if (!u) return [];
      const { data } = await get_ticket_assignees(u);
      return Array.isArray(data) ? data : [];
    } catch (ex) {
      console.log(ex);
      return rejectWithValue(ex?.response?.data ?? ex?.message ?? ex);
    }
  }
);

// ===== Phase 15: fetch history =====
export const getTicketHistory = createAsyncThunk(
  "tickets/getTicketHistory",
  async (ticket_id, { rejectWithValue }) => {
    try {
      const { data } = await get_ticket_history(ticket_id);
      return Array.isArray(data) ? data : [];
    } catch (ex) {
      console.log(ex);
      return rejectWithValue(ex?.response?.data ?? ex?.message ?? ex);
    }
  }
);

export const ticketSlice = createSlice({
  name: "tickets",
  initialState,
  reducers: {
    clearTicketsError: (state) => {
      state.error = null;
    },
    // ✅ optional helper
    clearAssignees: (state) => {
      state.assignees = [];
      state.assigneesLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // ===== GET TICKET HISTORY =====
      .addCase(getTicketHistory.pending, (state) => {
        state.historyLoading = true;
        state.history = [];
      })
      .addCase(getTicketHistory.fulfilled, (state, { payload }) => {
        state.historyLoading = false;
        state.history = payload;
      })
      .addCase(getTicketHistory.rejected, (state) => {
        state.historyLoading = false;
        state.history = [];
      })

      // ===== LIST =====
      .addCase(getTicketsList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTicketsList.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.tickets = normalizeTicketsList(payload);
      })
      .addCase(getTicketsList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? action.error?.message ?? "Request failed";
      })

      // ===== CREATE =====
      .addCase(addNewTicket.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addNewTicket.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addNewTicket.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? action.error?.message ?? "Request failed";
      })

      // ===== REPLY (legacy) =====
      .addCase(replyTicket.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(replyTicket.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(replyTicket.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? action.error?.message ?? "Request failed";
      })

      // ===== GET BY ID =====
      .addCase(getTicketById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTicketById.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.ticket = normalizeTicket(payload);
      })
      .addCase(getTicketById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? action.error?.message ?? "Request failed";
      })

      // ===== CLOSE (legacy) =====
      .addCase(closeTicket.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(closeTicket.fulfilled, (state) => {
        state.loading = false;
        if (Array.isArray(state.ticket) && state.ticket.length > 0) {
          state.ticket[0] = { ...state.ticket[0], status: "closed" };
        } else if (state.ticket && typeof state.ticket === "object") {
          state.ticket = { ...state.ticket, status: "closed" };
        }
      })
      .addCase(closeTicket.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? action.error?.message ?? "Request failed";
      })

      // ===== Phase 13: ASSIGNEES =====
      .addCase(getTicketAssignees.pending, (state) => {
        state.assigneesLoading = true;
      })
      .addCase(getTicketAssignees.fulfilled, (state, { payload }) => {
        state.assigneesLoading = false;
        state.assignees = Array.isArray(payload) ? payload : [];
      })
      .addCase(getTicketAssignees.rejected, (state) => {
        state.assigneesLoading = false;
        state.assignees = [];
      })

      // ===== Inline ops errors (keep UX smooth) =====
      .addCase(claimTicket.rejected, (state, action) => {
        state.error = action.payload ?? action.error?.message ?? "Request failed";
      })
      .addCase(assignTicket.rejected, (state, action) => {
        state.error = action.payload ?? action.error?.message ?? "Request failed";
      })
      .addCase(replyTicketInline.rejected, (state, action) => {
        state.error = action.payload ?? action.error?.message ?? "Request failed";
      })
      .addCase(closeTicketInline.rejected, (state, action) => {
        state.error = action.payload ?? action.error?.message ?? "Request failed";
      })

      // ✅ Optional: optimistic local update for claim/assign (refresh still happens in UI)
      .addCase(claimTicket.fulfilled, (state, { payload }) => {
        const { ticket_id, data } = payload || {};
        const assigned_to_id = data?.assigned_to_id;

        // update list
        if (Array.isArray(state.tickets)) {
          state.tickets = state.tickets.map((t) => {
            if (t?.id !== ticket_id) return t;
            return {
              ...t,
              assigned_to: t.assigned_to ?? { id: assigned_to_id },
              assigned_at: data?.assigned_at ?? t.assigned_at,
            };
          });
        }

        // update detail (first message row contains assigned_to in our backend)
        if (Array.isArray(state.ticket) && state.ticket.length > 0) {
          state.ticket[0] = {
            ...state.ticket[0],
            assigned_to: state.ticket[0]?.assigned_to ?? { id: assigned_to_id },
            assigned_at: data?.assigned_at ?? state.ticket[0]?.assigned_at,
          };
        }
      })
      .addCase(assignTicket.fulfilled, (state, { payload }) => {
        const { ticket_id, assigned_to_id } = payload || {};

        // update list
        if (Array.isArray(state.tickets)) {
          state.tickets = state.tickets.map((t) => {
            if (t?.id !== ticket_id) return t;

            let assigned_to = t.assigned_to;
            if (assigned_to_id == null) {
              assigned_to = null;
            } else {
              const found = Array.isArray(state.assignees)
                ? state.assignees.find((u) => u?.id === assigned_to_id)
                : null;
              assigned_to = found ? found : { id: assigned_to_id };
            }

            return { ...t, assigned_to };
          });
        }

        // update detail
        if (Array.isArray(state.ticket) && state.ticket.length > 0) {
          let assigned_to = state.ticket[0]?.assigned_to;
          if (assigned_to_id == null) {
            assigned_to = null;
          } else {
            const found = Array.isArray(state.assignees)
              ? state.assignees.find((u) => u?.id === assigned_to_id)
              : null;
            assigned_to = found ? found : { id: assigned_to_id };
          }

          state.ticket[0] = { ...state.ticket[0], assigned_to };
        }
      });
  },
});

export const { clearTicketsError, clearAssignees } = ticketSlice.actions;

export default ticketSlice.reducer;
